import { Settings } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Widget } from '~/components/MyWorkspace/Widget';

import { WidgetProps } from '../../data/widgets';

import { ConfigureQuickLinksDialogContent } from './components/ConfigureQuickLinksDialogContent';
import { QuickLinksItemsList } from './components/QuickLinksItemsList';
import { useQuickLinksWidget } from './useQuickLinksWidget';
import { useSxStyles } from './useStyles';

export interface Props extends WidgetProps {}

export const QuickLinksWidget: React.FC<Props> = ({ title, config }) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const {
    quickLinkViewItems,
    isLoadingQuickLinks,
    failedToLoadQuickLinks,
    setNoOfFittingListItems,
    configureDialog,
    showAllDialog,
    isCompanyWorkspace,
  } = useQuickLinksWidget(containerRef, config?.workspaceType);

  return (
    <>
      <Widget
        title={title || t('quickLinks')}
        dataCy="quick-links-widget"
        content={
          quickLinkViewItems.active?.length ? (
            <QuickLinksItemsList
              quickLinks={quickLinkViewItems.fitting}
              isLoadingQuickLinks={isLoadingQuickLinks}
              failedToLoadQuickLinks={failedToLoadQuickLinks}
              onNoOfFittingListItemsChange={setNoOfFittingListItems}
              showFullDescription={false}
            />
          ) : (
            <Box sx={sxStyles.root}>
              <Typography variant="body2" sx={sxStyles.errorMessage}>
                {t('quickLinksEmpty')}
              </Typography>
            </Box>
          )
        }
        actions={
          <>
            <Button
              size="small"
              color="primary"
              disabled={!showAllDialog.enabled}
              onClick={showAllDialog.state.open}
              data-cy="show-all"
            >
              {t('showAll')}
            </Button>
            {!isCompanyWorkspace && (
              <Button
                size="small"
                color="primary"
                startIcon={<Settings />}
                disabled={!configureDialog.enabled}
                onClick={configureDialog.state.open}
                data-cy="configure"
              >
                {t('configure')}
              </Button>
            )}
          </>
        }
      />

      <Dialog
        open={configureDialog.state.isOpen}
        onClose={configureDialog.state.close}
        fullWidth={true}
        data-cy="configure-list-widget-dialog"
      >
        <ConfigureQuickLinksDialogContent
          quickLinkItems={quickLinkViewItems.all}
          onClose={configureDialog.state.close}
          workspaceType={config?.workspaceType}
        />
      </Dialog>
      <Dialog
        open={showAllDialog.state.isOpen}
        onClose={showAllDialog.state.close}
        fullWidth={true}
        data-cy="all-list-widget-dialog"
      >
        <DialogTitle>{t('quickLinks')}</DialogTitle>
        <DialogContent sx={sxStyles.dialogContent}>
          <QuickLinksItemsList
            quickLinks={quickLinkViewItems.active}
            isLoadingQuickLinks={isLoadingQuickLinks}
            failedToLoadQuickLinks={failedToLoadQuickLinks}
            showFullDescription={true}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
